<template>
  <b-modal
    id="modalSharing"
    centered
    scrollable
    :title="`Share ${ shareType[0] + shareType.slice(1).toLowerCase() }: ${ campaignName }`"
    modal-class="share-modal"
    data-test="creative-share-modal"
  >
    <div
      class="border rounded p-4 mb-3"
    >
      <label
        for="user-email-input"
        class="font-weight-bold fn-size-15"
      >
        Add User
      </label>
      <b-input-group>
        <b-form-input
          id="user-email-input"
          placeholder="Email Address"
          v-model="userEmail"
          type="email"
          :class="[enteredEmailExists && !emailInSharedWithList ? '' : 'is-invalid']"
        />
        <template #append>
          <b-button
            data-test="add-user-email"
            variant="transparent"
            @click="addEmailToShareList"
            :disabled="isAddDisabled"
            :class="{ 'btn-loading black-loader': isAddButtonLoading }"
          >
            Add
          </b-button>
        </template>
      </b-input-group>
      <div
        style="font-size: 0.75rem;"
        class="mt-2"
      >
        <p
          v-if="emailInSharedWithList && !isAddButtonLoading"
          :class="{'invalid': emailInSharedWithList}"
        >
          The entered email is already in the "Share With" list below.
        </p>
        <p
          v-else-if="!enteredEmailExists && !isAddButtonLoading"
          :class="{'invalid': enteredEmailExists}"
        >
          We couldn't find an account associated with that email. Create an account below.
        </p>
        <p
          v-else-if="enteredEmailExists && !emailInSharedWithList && !isAddButtonLoading"
        >
          Enter the email addresses of the people you wish to share the {{ shareType.toLowerCase() }} with. <span v-if="!isSelfServiceCampaign"> If there is no account with the email address entered, you will be prompted to create one. </span>
        </p>
      </div>
      <b-form
        v-if="!enteredEmailExists && !isAddButtonLoading"
        @submit="createNewUserAccount"
      >
        <div
          class="d-flex"
        >
          <b-form-input
            id="first-name"
            class="mr-3"
            v-model="newUserForm.firstName"
            placeholder="First Name"
            required
          />
          <b-form-input
            id="last-name"
            v-model="newUserForm.lastName"
            placeholder="Last Name"
            required
          />
        </div>
        <b-form-input
          class="my-3"
          id="company-name"
          v-model="newUserForm.companyName"
          placeholder="Company Name"
          required
        />
        <b-button
          type="submit"
          id="create-account"
          variant="primary"
          size="sm"
        >
          Create Account
        </b-button>
      </b-form>
    </div>
    <div
      v-if="isShareWithEmailsListLoading"
      class="loader mx-auto"
      style="height: 300px"
    />
    <div
      class="border rounded p-4"
      v-else-if="alreadySharedWithEmails.length > 0 || totalSharedWithEmails.length > 0"
    >
      <p
        class="font-weight-bold fn-size-15"
      >
        Share With
      </p>
      <div
        id="share-with-emails-list-container"
      >
        <ul
          id="share-with-emails-list"
          class="m-0 p-0"
        >
          <share-modal-emails-list-item
            v-for="(email, index) in totalSharedWithEmails"
            class="border share-with-emails-item"
            :class="{
              'rounded': totalSharedWithEmails.length === 1,
              'top-card': totalSharedWithEmails.length > 1 && index === 0,
              'bottom-card': totalSharedWithEmails.length > 1 && index === totalSharedWithEmails.length -1,
              'borderless-bottom': totalSharedWithEmails.length > 1 && index !== totalSharedWithEmails.length -1,
            }"
            :key="index"
            :index="index"
            :resend-enabled="!isSelfServiceCampaign && alreadySharedWithEmails.includes(email)"
            :campaign="campaign"
            :share-type="shareType"
            :creative="creative"
            :email="email"
            @removeEmail="removeEmail"
          />
        </ul>
      </div>
    </div>
    <template #modal-footer>
      <span
        v-if="shareType === 'SHOWCASE'"
        class="mr-auto"
      >
        {{ sharedCount }}/{{ feedTotalCount }} items will be shared.
      </span>
      <b-button
        id="share-modal-invite"
        variant="primary"
        size="sm"
        type="button"
        @click="inviteEmails($event)"
        :disabled="isInviteDisabled"
        :class="{ 'btn-loading': isInviteButtonLoading }"
      >
        Share
      </b-button>
    </template>
  </b-modal>
</template>
<script>
import signUtils from '@/core/signUtils';
import { mapActions, mapState } from 'vuex';
import * as accountTypes from '@/modules/account/store/types';
import * as shareTypes from '@/modules/share/store/types';
import { v4 as uuidv4 } from 'uuid';
import * as authenticationTypes from '@/modules/authentication/store/types';
import * as contentTypes from '@/modules/content/store/types';

import ShareModalEmailsListItem from '@/modules/share/components/ShareModalEmailsListItem';

function initialState() {
  return {
    newUserForm: {
      firstName: '',
      lastName: '',
      companyName: '',
    },
    isShareWithEmailsListLoading: true,
    emailInSharedWithList: false,
    enteredEmailExists: true,
    isAddButtonLoading: false,
    isInviteButtonLoading: false,
    userEmail: '',
    alreadySharedWithEmails: [],
    existingUsersShareList: [],
    notExistingUsersShareList: [],
    successfullySignedUpNewUsers: [],
    failedSignedUpNewUsers: [],
  };
}

export default {
  name: 'ShareModal',
  components: {
    ShareModalEmailsListItem,
  },
  props: {
    shareType: {
      type: String,
      default: '',
    },
  },
  data() {
    return initialState();
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'feedTotalCount',
      'sharedCount',
      'creative',
    ]),
    ...mapState('account', {
      sharedByFirstName: 'firstName',
      sharedByLastName: 'lastName',
    }),
    isAddDisabled() {
      return !this.isEmailValid() || this.isAddButtonLoading;
    },
    isInviteDisabled() {
      return (this.existingUsersShareList.length === 0 && this.notExistingUsersShareList.length === 0)
      || this.failedSignedUpNewUsers.length > 0
      || this.isInviteButtonLoading;
    },
    campaignName() {
      return this.campaign?.name || '';
    },
    totalSharedWithEmails() {
      const notExistingUsersShareListEmails = this.notExistingUsersShareList.map((item) => item.email) || [];
      return [...this.existingUsersShareList, ...notExistingUsersShareListEmails, ...this.alreadySharedWithEmails];
    },
    isSelfServiceCampaign() {
      return this.campaign?.type === 'SELF_SERVICE';
    },
  },
  methods: {
    ...mapActions('account', [
      accountTypes.GET_USER_EMAIL_EXIST,
    ]),
    ...mapActions('share', [
      shareTypes.FETCH_SHARE_WITH_EMAILS_LIST,
      shareTypes.UPDATE_SHARE_WITH_EMAILS_LIST,
      shareTypes.REMOVE_EMAIL_FROM_SHARE_WITH,
    ]),
    ...mapActions('content', [
      contentTypes.FETCH_SHARED_COUNT,
    ]),
    ...mapActions('authentication', [
      authenticationTypes.SIGN_UP,
    ]),
    resetData() {
      Object.assign(this.$data, initialState());
    },
    trimEmail() {
      this.userEmail = this.userEmail.trim();
    },
    encodedEmail() {
      const splittedEmail = this.userEmail.split('@');
      splittedEmail[0] = encodeURIComponent(splittedEmail[0]);
      return splittedEmail.join('@');
    },
    isEmailValid() {
      return signUtils.isEmailValid(this.userEmail);
    },
    async getAlreadySharedWithEmails() {
      this.alreadySharedWithEmails = await this.FETCH_SHARE_WITH_EMAILS_LIST({ campaignId: this.campaign?.id, shareType: this.shareType });
      this.isShareWithEmailsListLoading = false;
    },
    addEmailToShareList() {
      this.isAddButtonLoading = true;
      this.enteredEmailExists = true;
      this.trimEmail();
      if (this.totalSharedWithEmails.includes(this.userEmail)) {
        this.emailInSharedWithList = true;
        this.isAddButtonLoading = false;
        return;
      }

      if (this.isSelfServiceCampaign) {
        this.existingUsersShareList.push(this.userEmail);
        this.enteredEmailExists = true;
        this.userEmail = '';
        this.isAddButtonLoading = false;
        return;
      }

      this.GET_USER_EMAIL_EXIST({ email: this.encodedEmail() })
        .then((status) => {
          if (status) {
            this.existingUsersShareList.push(this.userEmail);
            this.enteredEmailExists = true;
            this.userEmail = '';
            this.isAddButtonLoading = false;
            return;
          }
          this.enteredEmailExists = false;
          this.isAddButtonLoading = false;
        });
      this.emailInSharedWithList = false;
    },
    removeEmail(email) {
      if (this.alreadySharedWithEmails.includes(email)) {
        this.REMOVE_EMAIL_FROM_SHARE_WITH({
          campaignId: this.campaign.id,
          shareType: this.shareType,
          body: {
            sharedWithEmail: email,
          },
        })
          .then(() => {
            const index = this.alreadySharedWithEmails.findIndex((existingEmail) => existingEmail === email);
            this.alreadySharedWithEmails.splice(index, 1);
          });
        return;
      }
      if (this.existingUsersShareList.includes(email)) {
        const index = this.existingUsersShareList.findIndex((existingEmail) => existingEmail === email);
        this.existingUsersShareList.splice(index, 1);
        return;
      }
      this.notExistingUsersShareList.forEach((notExistingEmail, index) => {
        if (notExistingEmail.email === email) {
          this.notExistingUsersShareList.splice(index, 1);
        }
      });
    },
    createNewUserAccount() {
      const { firstName, lastName, companyName } = this.newUserForm;
      this.notExistingUsersShareList.push({
        firstName,
        lastName,
        companyName,
        email: this.userEmail,
      });
      this.newUserForm = {
        firstName: '',
        lastName: '',
        companyName: '',
      };
      this.userEmail = '';
      this.enteredEmailExists = true;
    },
    async inviteEmails(bvModalEvent) {
      bvModalEvent.preventDefault();
      this.isInviteButtonLoading = true;
      if (this.notExistingUsersShareList.length > 0) {
        const signUpPromises = this.notExistingUsersShareList.map((user) => {
          const authCode = uuidv4();
          return this.SIGN_UP({
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            companyName: user.companyName,
            signUpType: 'SHARE',
            authCode,
            password: this.convertAuthCodeToTempPassword(authCode),
            emailProperties: this.constructEmailProperties(),
          })
            .then(() => {
              this.successfullySignedUpNewUsers.push(user.email);
            })
            .catch(() => {
              this.failedSignedUpNewUsers.push(user.email);
            });
        });
        await Promise.all(signUpPromises);
      }
      if (this.failedSignedUpNewUsers.length > 0) {
        this.isInviteButtonLoading = false;
        return;
      }
      this.UPDATE_SHARE_WITH_EMAILS_LIST({
        campaignId: this.campaign.id,
        body: {
          type: this.shareType,
          creativeKey: this.creative.creativeKey,
          sharedWithEmails: [...this.successfullySignedUpNewUsers, ...this.existingUsersShareList],
        },
      })
        .then(() => {
          [...this.successfullySignedUpNewUsers, ...this.existingUsersShareList].forEach((email) => {
            this.alreadySharedWithEmails.push(email);
          });

          this.existingUsersShareList = [];
          this.notExistingUsersShareList = [];
          this.successfullySignedUpNewUsers = [];
          this.failedSignedUpNewUsers = [];
          this.isInviteButtonLoading = false;
        });
    },
    constructEmailProperties() {
      return `{"advertiser_id": ${this.campaign.advertiserId}, "shared_by": "${this.sharedByFirstName} ${this.sharedByLastName}",
      "share_type": "${this.shareType[0] + this.shareType.slice(1).toLowerCase()}", "campaign": {"id": ${this.campaign.id}, "name": "${this.campaign.name}"}}`;
    },
    convertAuthCodeToTempPassword(authCode) {
      const reverseString = (string) => string.split('').reverse().join('');
      const uuid = authCode.split('-')[4];
      return `${reverseString(uuid)}-Sm1`;
    },
  },
  mounted() {
    this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
      if (modalId === 'modalSharing') {
        this.getAlreadySharedWithEmails();
        if (this.shareType === 'SHOWCASE') this.FETCH_SHARED_COUNT();
      }
    });
    this.$root.$on('bv::modal::hide', (bvEvent, modalId) => {
      if (modalId === 'modalSharing') {
        this.resetData();
      }
    });
  },
  destroyed() {
    this.$root.$off('bv::modal::show');
    this.$root.$off('bv::modal::hidden');
  },
};
</script>
<style scoped lang="scss">
.fn-size-15 {
font-size: 15px;
}
.black-loader::after {
  border: 2px solid #000;
}
#share-with-emails-list-container {
  min-height: 300px;
  max-height: 40vh;
  overflow: scroll;
}
.borderless-bottom {
  border-bottom: 0px solid transparent !important;
}
.top-card {
border-radius: 4px 4px 0px 0px;
margin: 0;
border-bottom: 0px solid transparent;
}
.bottom-card {
border-radius: 0px 0px 4px 4px;
margin: 0;
border-top: 0px solid transparent;
}
</style>
<style lang="scss">
.share-modal .modal-dialog {
    min-width: 600px;
}
</style>
